// utils/notificationUtils.js

// ============================================
// DEVICE DETECTION
// ============================================

// Check if device is mobile
export function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
  
  // Check if iOS device
  export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }
  
  // Check if Android device
  export function isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }
  
  // Check if PWA installed
  export function isPWAInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }
  
  // ============================================
  // NOTIFICATION SETTINGS
  // ============================================
  
  // Get notification settings from localStorage
  export function getNotificationSettings() {
    const defaultSettings = {
      enabled: true,
      sound: true,
      desktop: true,
      inApp: true,
      vibrate: false,
      quietHours: false,
      quietStart: '22:00',
      quietEnd: '08:00'
    };
    
    const saved = localStorage.getItem('notification_settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch (e) {
        return defaultSettings;
      }
    }
    
    return defaultSettings;
  }
  
  // Save notification settings
  export function saveNotificationSettings(settings) {
    localStorage.setItem('notification_settings', JSON.stringify(settings));
  }
  
  // Check if user should receive notification based on settings
  export function shouldSendNotification(notificationType = 'default') {
    // Check if browser supports notifications
    if (!('Notification' in window)) return false;
    
    // Check permission
    if (Notification.permission !== 'granted') return false;
    
    // Check user settings
    const settings = getNotificationSettings();
    if (!settings.enabled) return false;
    
    // Check quiet hours
    if (settings.quietHours) {
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      if (currentTime >= settings.quietStart && currentTime <= settings.quietEnd) {
        return false;
      }
    }
    
    return true;
  }
  
  // ============================================
  // NOTIFICATION PERMISSIONS
  // ============================================
  
  // Create custom confirm dialog (replaces window.confirm)
  function showConfirmDialog(message, onConfirm, onCancel) {
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
    dialog.innerHTML = `
      <div class="bg-white rounded-xl max-w-sm w-full p-6 animate-scale-up">
        <div class="text-center mb-4">
          <div class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span class="text-3xl">🔔</span>
          </div>
          <p class="text-gray-700">${message}</p>
        </div>
        <div class="flex gap-3">
          <button id="confirm-yes" class="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium">Enable</button>
          <button id="confirm-no" class="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium">Cancel</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    const yesBtn = document.getElementById('confirm-yes');
    const noBtn = document.getElementById('confirm-no');
    
    const cleanup = () => {
      dialog.remove();
      yesBtn?.removeEventListener('click', handleYes);
      noBtn?.removeEventListener('click', handleNo);
    };
    
    const handleYes = () => {
      cleanup();
      onConfirm();
    };
    
    const handleNo = () => {
      cleanup();
      if (onCancel) onCancel();
    };
    
    yesBtn?.addEventListener('click', handleYes);
    noBtn?.addEventListener('click', handleNo);
    
    // Close when clicking outside
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        cleanup();
        if (onCancel) onCancel();
      }
    });
  }
  
  // Create custom alert dialog (replaces window.alert)
  function showAlertDialog(message, onClose) {
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
    dialog.innerHTML = `
      <div class="bg-white rounded-xl max-w-sm w-full p-6 animate-scale-up">
        <div class="text-center">
          <div class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span class="text-3xl">ℹ️</span>
          </div>
          <p class="text-gray-700 mb-6">${message}</p>
          <button id="alert-ok" class="w-full py-2 bg-orange-500 text-white rounded-lg font-medium">OK</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    const okBtn = document.getElementById('alert-ok');
    
    const cleanup = () => {
      dialog.remove();
      okBtn?.removeEventListener('click', handleOk);
    };
    
    const handleOk = () => {
      cleanup();
      if (onClose) onClose();
    };
    
    okBtn?.addEventListener('click', handleOk);
    
    // Close when clicking outside
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        cleanup();
        if (onClose) onClose();
      }
    });
  }
  
  // Request notification permission with mobile-specific handling
  export async function requestNotificationPermission(showPrompt = true) {
    const isMobile = isMobileDevice();
    
    if (!('Notification' in window)) {
      // console.warn('Notifications not supported');
      if (showPrompt && isMobile) {
        showMobileNotificationGuide();
      }
      return false;
    }
    
    // Check if already granted
    if (Notification.permission === 'granted') {
      return true;
    }
    
    // Check if denied
    if (Notification.permission === 'denied') {
      if (showPrompt) {
        if (isMobile) {
          showMobileNotificationGuide();
        } else {
          showAlertDialog('Notifications are blocked. Please enable them in your browser settings to receive real-time updates.');
        }
      }
      return false;
    }
    
    // iOS requires user interaction before requesting permission
    if (isIOS() && showPrompt) {
      return new Promise((resolve) => {
        showConfirmDialog(
          '🔔 Enable Notifications\n\nGet real-time alerts about orders, messages, and important updates.',
          async () => {
            try {
              const permission = await Notification.requestPermission();
              const granted = permission === 'granted';
              
              if (granted && isMobile) {
                showInAppToast({
                  title: '✅ Notifications Enabled',
                  message: 'You will now receive real-time updates',
                  type: 'success'
                });
              }
              resolve(granted);
            } catch (error) {
              // console.error('Error requesting permission:', error);
              resolve(false);
            }
          },
          () => resolve(false)
        );
      });
    }
    
    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      
      if (granted && isMobile) {
        showInAppToast({
          title: '✅ Notifications Enabled',
          message: 'You will now receive real-time updates',
          type: 'success'
        });
      }
      
      return granted;
    } catch (error) {
      // console.error('Error requesting permission:', error);
      return false;
    }
  }
  
  // Show mobile notification guide
  export function showMobileNotificationGuide() {
    const isIOSDevice = isIOS();
    const isAndroidDevice = isAndroid();
    
    let message = '';
    let steps = [];
    
    if (isIOSDevice) {
      message = 'To enable notifications on iOS:';
      steps = [
        '1. Open Settings app',
        '2. Scroll down and tap "Safari"',
        '3. Tap "Notifications"',
        '4. Find this website and enable notifications',
        '5. Or install our app for better experience'
      ];
    } else if (isAndroidDevice) {
      message = 'To enable notifications on Android:';
      steps = [
        '1. Tap the lock/info icon in address bar',
        '2. Tap "Notifications"',
        '3. Select "Allow"',
        '4. Refresh the page'
      ];
    } else {
      message = 'To enable notifications:';
      steps = [
        '1. Click the lock/info icon in address bar',
        '2. Find "Notifications"',
        '3. Select "Allow"',
        '4. Refresh the page'
      ];
    }
    
    const guide = document.createElement('div');
    guide.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
    guide.innerHTML = `
      <div class="bg-white rounded-xl max-w-sm w-full p-6 animate-scale-up">
        <div class="text-center mb-4">
          <div class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span class="text-3xl">🔔</span>
          </div>
          <h3 class="text-lg font-bold text-gray-900">Enable Notifications</h3>
          <p class="text-sm text-gray-600 mt-1">${message}</p>
        </div>
        <div class="space-y-2 mb-6">
          ${steps.map(step => `<p class="text-sm text-gray-700">${step}</p>`).join('')}
        </div>
        <button id="close-guide" class="w-full py-3 bg-orange-500 text-white rounded-lg font-medium">
          Got it
        </button>
      </div>
    `;
    
    document.body.appendChild(guide);
    
    const closeGuide = () => {
      guide.remove();
    };
    
    document.getElementById('close-guide')?.addEventListener('click', closeGuide);
    guide.addEventListener('click', (e) => {
      if (e.target === guide) closeGuide();
    });
  }
  
  // ============================================
  // IN-APP TOAST NOTIFICATIONS (FOR MOBILE)
  // ============================================
  
  // Store for pending notifications
  let pendingNotifications = [];
  
  // Show in-app toast notification
  export function showInAppToast(options) {
    const settings = getNotificationSettings();
    if (!settings.inApp) return;
    
    // Dispatch event for toast container
    const event = new CustomEvent('showToast', {
      detail: {
        id: Date.now(),
        title: options.title,
        message: options.message,
        type: options.type || 'info',
        duration: options.duration || 5000,
        onClick: options.onClick,
        onClose: options.onClose
      }
    });
    window.dispatchEvent(event);
  }
  
  // Store notification for later (when user returns)
  export function storeNotificationForLater(title, options) {
    pendingNotifications.push({
      title,
      options,
      timestamp: Date.now(),
      id: Date.now()
    });
    
    // Limit stored notifications to 50
    if (pendingNotifications.length > 50) {
      pendingNotifications = pendingNotifications.slice(-50);
    }
    
    // Store in localStorage
    localStorage.setItem('pending_notifications', JSON.stringify(pendingNotifications));
  }
  
  // Get pending notifications
  export function getPendingNotifications() {
    const stored = localStorage.getItem('pending_notifications');
    if (stored) {
      try {
        pendingNotifications = JSON.parse(stored);
        return pendingNotifications;
      } catch (e) {
        return [];
      }
    }
    return [];
  }
  
  // Clear pending notifications
  export function clearPendingNotifications() {
    pendingNotifications = [];
    localStorage.removeItem('pending_notifications');
  }
  
  // Show all pending notifications
  export function showPendingNotifications() {
    const pending = getPendingNotifications();
    if (pending.length === 0) return;
    
    // Show each pending notification as toast
    pending.forEach(notification => {
      setTimeout(() => {
        showInAppToast({
          title: notification.title,
          message: notification.options?.body || '',
          type: notification.options?.type
        });
      }, 1000);
    });
    
    // Clear after showing
    setTimeout(() => {
      clearPendingNotifications();
    }, 5000);
  }
  
  // ============================================
  // VIBRATION (MOBILE)
  // ============================================
  
  // Vibrate device (mobile only)
  export function vibrate(pattern = [200, 100, 200]) {
    const settings = getNotificationSettings();
    if (!settings.vibrate) return;
    
    if (isMobileDevice() && window.navigator.vibrate) {
      window.navigator.vibrate(pattern);
    }
  }
  
  // ============================================
  // NOTIFICATION SOUND
  // ============================================
  
  // Play notification sound
  export function playNotificationSound(type = 'default') {
    const settings = getNotificationSettings();
    if (!settings.sound) return;
    
    const sounds = {
      order: '/sounds/order.mp3',
      message: '/sounds/message.mp3',
      favorite: '/sounds/favorite.mp3',
      boost: '/sounds/boost.mp3',
      payment: '/sounds/payment.mp3',
      dispute: '/sounds/dispute.mp3',
      default: '/sounds/notification.mp3'
    };
    
    const soundUrl = sounds[type] || sounds.default;
    
    try {
      const audio = new Audio(soundUrl);
      audio.volume = 0.5;
      audio.play().catch(e =>  console.log('Sound play failed:', e));
    } catch (error) {
      // console.error('Error playing sound:', error);
    }
  }
  
  // ============================================
  // MAIN SHOW NOTIFICATION FUNCTION
  // ============================================
  
  // Show notification with settings check and mobile support
  export function showNotification(title, options = {}) {
    const isMobile = isMobileDevice();
    const settings = getNotificationSettings();
    
    // Always show in-app toast on mobile or if enabled
    if (isMobile || settings.inApp) {
      showInAppToast({
        title,
        message: options.body,
        type: options.type,
        duration: 5000,
        onClick: options.onClick
      });
      
      // Store notification for later (in case user missed it)
      if (isMobile && document.hidden) {
        storeNotificationForLater(title, options);
      }
    }
    
    // For desktop, also show system notification if allowed
    if (!isMobile && shouldSendNotification(options.type)) {
      try {
        const notification = new Notification(title, {
          body: options.body,
          icon: options.icon || '/favicon.ico',
          tag: options.tag,
          requireInteraction: options.requireInteraction || false,
          silent: !settings.sound,
          vibrate: settings.vibrate ? [200, 100, 200] : undefined
        });
        
        if (options.onClick) {
          notification.onclick = () => {
            window.focus();
            options.onClick();
            notification.close();
          };
        }
        
        setTimeout(() => notification.close(), 5000);
      } catch (error) {
        // console.error('Error showing notification:', error);
      }
    }
    
    // Play sound
    if (settings.sound && options.type) {
      playNotificationSound(options.type);
    }
    
    // Vibrate on mobile
    if (isMobile && settings.vibrate && options.type !== 'system') {
      vibrate();
    }
    
    return true;
  }
  
  // ============================================
  // INITIALIZATION
  // ============================================
  
  // Create desktop notification prompt
  function createDesktopNotificationPrompt() {
    const prompt = document.createElement('div');
    prompt.className = 'fixed bottom-4 right-4 z-50 animate-slide-up';
    prompt.innerHTML = `
      <div class="bg-white rounded-xl shadow-xl border p-4 max-w-sm">
        <div class="flex gap-3">
          <div class="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <svg class="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
            </svg>
          </div>
          <div class="flex-1">
            <h4 class="font-semibold text-gray-900">Enable Notifications</h4>
            <p class="text-sm text-gray-600 mt-1">Get real-time alerts for orders, messages, and updates</p>
            <div class="flex gap-2 mt-3">
              <button id="enable-notifications" class="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg">Enable</button>
              <button id="dismiss-notifications" class="px-3 py-1.5 text-gray-600 text-sm rounded-lg">Maybe Later</button>
            </div>
          </div>
          <button id="close-notifications" class="text-gray-400 hover:text-gray-600">✕</button>
        </div>
      </div>
    `;
    document.body.appendChild(prompt);
    
    const enableBtn = document.getElementById('enable-notifications');
    const dismissBtn = document.getElementById('dismiss-notifications');
    const closeBtn = document.getElementById('close-notifications');
    
    enableBtn?.addEventListener('click', async () => {
      await requestNotificationPermission(true);
      prompt.remove();
    });
    
    dismissBtn?.addEventListener('click', () => {
      localStorage.setItem('notification_prompt_dismissed', 'true');
      prompt.remove();
    });
    
    closeBtn?.addEventListener('click', () => {
      prompt.remove();
    });
  }
  
  // Initialize notifications on page load
  export function initNotifications() {
    const isMobile = isMobileDevice();
    
    // Check if already granted
    if (Notification.permission === 'granted') {
      // console.log('✅ Notifications already enabled');
      // Show any pending notifications
      setTimeout(() => showPendingNotifications(), 2000);
      return true;
    }
    
    // Check if user has dismissed prompt
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed === 'true') {
      return false;
    }
    
    // For mobile, show guide instead of prompt
    if (isMobile) {
      // Check if we've shown guide before
      const guideShown = localStorage.getItem('notification_guide_shown');
      if (!guideShown) {
        setTimeout(() => {
          if (Notification.permission === 'default') {
            showMobileNotificationGuide();
            localStorage.setItem('notification_guide_shown', 'true');
          }
        }, 5000);
      }
      return false;
    }
    
    // Desktop: Show gentle prompt after user interaction
    const handleUserInteraction = () => {
      setTimeout(() => {
        if (Notification.permission === 'default') {
          createDesktopNotificationPrompt();
        }
      }, 3000);
      
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('scroll', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('scroll', handleUserInteraction, { once: true });
    
    return false;
  }
  
  // ============================================
  // DEFAULT EXPORT
  // ============================================
  
  const notificationUtils = {
    isMobileDevice,
    isIOS,
    isAndroid,
    isPWAInstalled,
    getNotificationSettings,
    saveNotificationSettings,
    shouldSendNotification,
    requestNotificationPermission,
    showMobileNotificationGuide,
    showInAppToast,
    storeNotificationForLater,
    getPendingNotifications,
    clearPendingNotifications,
    showPendingNotifications,
    vibrate,
    playNotificationSound,
    showNotification,
    initNotifications
  };
  
  export default notificationUtils;